import {
  type Audience,
  FIELD_CONDITION_LABELS,
  type FieldCondition,
  type FieldConditionOp,
  OPS_BY_FIELD_TYPE,
} from '@fundline/shared/schemas/campaigns';
import { type ReactNode, useId, useState } from 'react';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL } from '../../components/ui/Field.tsx';
import { useImportsQuery } from '../imports/use-imports.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { type FieldDefinition, useFieldDefinitionsQuery } from '../settings/use-fields.ts';
import { useTagsQuery } from '../settings/use-tags.ts';
import { conditionInput, conditionValue, needsValue, parseStates } from './audience-form.ts';
import { useMerchantSourcesQuery } from './use-campaigns.ts';

/**
 * Who a campaign goes to (M9 #4): tags, lead sources, imports, reps, states and field conditions. The
 * worker matches the audience and decides who is skipped; this only builds the filters.
 */

function toggle(list: readonly string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...list, value])] : list.filter((v) => v !== value);
}

function CheckList({
  legend,
  items,
  selected,
  onChange,
  empty,
  testId,
}: {
  legend: string;
  items: { value: string; label: string; hint?: string }[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  empty: string;
  testId?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-1" data-testid={testId}>
      <legend className="callout mb-1 font-semibold text-ink">{legend}</legend>
      {items.length === 0 ? <p className="footnote text-ink-muted">{empty}</p> : null}
      {items.length > 0 ? (
        <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md bg-surface p-2 ring-1 ring-hairline">
          {items.map((item) => (
            <label key={item.value} className="callout flex min-h-11 items-center gap-2 text-ink">
              <input
                type="checkbox"
                className="size-4 accent-brand"
                checked={selected.includes(item.value)}
                onChange={(e) => onChange(toggle(selected, item.value, e.target.checked))}
              />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.hint ? <span className="footnote text-ink-muted">{item.hint}</span> : null}
            </label>
          ))}
        </div>
      ) : null}
    </fieldset>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className="rounded-md ring-1 ring-hairline" open>
      <summary className="callout cursor-pointer px-3 py-2 font-semibold text-ink">{title}</summary>
      <div className="flex flex-col gap-3 px-3 pb-3">{children}</div>
    </details>
  );
}

export function AudienceBuilder({
  workspaceId,
  value,
  onChange,
}: {
  workspaceId: string;
  value: Audience;
  onChange: (next: Audience) => void;
}) {
  const tags = useTagsQuery(workspaceId);
  const imports = useImportsQuery(workspaceId);
  const members = useMembersQuery(workspaceId);
  const fields = useFieldDefinitionsQuery(workspaceId);
  const sources = useMerchantSourcesQuery(workspaceId);
  const statesId = useId();
  const [statesText, setStatesText] = useState(value.states.join(', '));
  const states = parseStates(statesText);
  const set = <K extends keyof Audience>(key: K, v: Audience[K]) =>
    onChange({ ...value, [key]: v });
  const tagMode: 'any' | 'all' = value.tagsAll.length > 0 ? 'all' : 'any';
  const chosenTags = tagMode === 'all' ? value.tagsAll : value.tagsAny;
  const tagItems = (tags.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  const activeFields = (fields.data ?? []).filter((f) => !f.archivedAt);

  return (
    <div className="flex flex-col gap-3" data-testid="audience-builder">
      <label className="callout flex min-h-11 items-center gap-2 text-ink">
        <input
          type="checkbox"
          className="size-4 accent-brand"
          checked={value.everyone}
          onChange={(e) => set('everyone', e.target.checked)}
        />
        Everyone in your workspace (the filters below still narrow it)
      </label>

      <Section title="Tags">
        <div className="fl-segmented" role="radiogroup" aria-label="Tag match">
          {(['any', 'all'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={tagMode === mode}
              className="fl-segmented__opt"
              onClick={() =>
                onChange(
                  mode === 'all'
                    ? { ...value, tagsAll: chosenTags, tagsAny: [] }
                    : { ...value, tagsAny: chosenTags, tagsAll: [] },
                )
              }
            >
              {mode === 'any' ? 'Any of these' : 'All of these'}
            </button>
          ))}
        </div>
        <CheckList
          legend="Merchants tagged"
          items={tagItems}
          selected={chosenTags}
          onChange={(next) => set(tagMode === 'all' ? 'tagsAll' : 'tagsAny', next)}
          empty={tags.isPending ? 'Loading…' : 'No tags yet. Add them in Settings → Tags.'}
          testId="audience-tags"
        />
        <CheckList
          legend="Leave out merchants tagged"
          items={tagItems}
          selected={value.excludeTags}
          onChange={(next) => set('excludeTags', next)}
          empty={tags.isPending ? 'Loading…' : 'No tags yet.'}
        />
      </Section>

      <Section title="Source and imports">
        <CheckList
          legend="Lead source"
          items={(sources.data ?? []).map((s) => ({ value: s, label: s }))}
          selected={value.sources}
          onChange={(next) => set('sources', next)}
          empty={sources.isPending ? 'Loading…' : 'No lead sources yet.'}
        />
        <CheckList
          legend="From these imports"
          items={(imports.data ?? [])
            .filter((i) => i.status === 'completed')
            .map((i) => ({
              value: i.id,
              label: i.fileName,
              hint: new Date(i.createdAt).toLocaleDateString(),
            }))}
          selected={value.importIds}
          onChange={(next) => set('importIds', next)}
          empty={imports.isPending ? 'Loading…' : 'No finished imports yet.'}
        />
      </Section>

      <Section title="Rep and state">
        <CheckList
          legend="Assigned to"
          items={(members.data ?? [])
            .filter((m) => m.status === 'active')
            .map((m) => ({ value: m.userId, label: m.fullName || m.email }))}
          selected={value.repIds}
          onChange={(next) => set('repIds', next)}
          empty={members.isPending ? 'Loading…' : 'No active members.'}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={statesId} className="callout font-semibold text-ink">
            In these states
          </label>
          <input
            id={statesId}
            className={CONTROL}
            placeholder="NY, NJ, FL"
            value={statesText}
            aria-invalid={states.ok ? undefined : true}
            onChange={(e) => {
              setStatesText(e.target.value);
              const parsed = parseStates(e.target.value);
              if (parsed.ok) set('states', parsed.states);
            }}
          />
          {states.ok ? (
            <p className="footnote text-ink-muted">Two-letter codes. Empty means any state.</p>
          ) : (
            <p className="footnote text-danger">{states.error}</p>
          )}
        </div>
      </Section>

      <Section title="Field conditions">
        {value.fields.map((c, i) => (
          <ConditionRow
            key={`${c.fieldId}-${i}`}
            fields={activeFields}
            condition={c}
            onChange={(next) =>
              set(
                'fields',
                value.fields.map((x, j) => (j === i ? next : x)),
              )
            }
            onRemove={() =>
              set(
                'fields',
                value.fields.filter((_, j) => j !== i),
              )
            }
          />
        ))}
        {activeFields.length > 0 && value.fields.length < 20 ? (
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const f = activeFields[0];
                if (!f) return;
                const op = (OPS_BY_FIELD_TYPE[f.type] ?? ['not_empty'])[0] ?? 'not_empty';
                set('fields', [...value.fields, { fieldId: f.id, op, value: null }]);
              }}
            >
              Add a condition
            </Button>
          </div>
        ) : null}
      </Section>
    </div>
  );
}

function ConditionRow({
  fields,
  condition,
  onChange,
  onRemove,
}: {
  fields: FieldDefinition[];
  condition: FieldCondition;
  onChange: (next: FieldCondition) => void;
  onRemove: () => void;
}) {
  const field = fields.find((f) => f.id === condition.fieldId);
  const type = field?.type ?? 'text';
  const ops = OPS_BY_FIELD_TYPE[type] ?? [];
  const [raw, setRaw] = useState(conditionInput(type, condition.value));
  const parsed = conditionValue(type, condition.op, raw);
  const update = (op: FieldConditionOp, text: string) => {
    setRaw(text);
    const v = conditionValue(type, op, text);
    onChange({ ...condition, op, value: v.ok ? v.value : null });
  };
  return (
    <div className="flex flex-wrap items-start gap-2" data-testid="audience-condition">
      <select
        aria-label="Field"
        className={`${CONTROL} w-auto min-w-40 flex-1`}
        value={condition.fieldId}
        onChange={(e) => {
          const next = fields.find((f) => f.id === e.target.value);
          const op = (OPS_BY_FIELD_TYPE[next?.type ?? 'text'] ?? ['not_empty'])[0] ?? 'not_empty';
          setRaw('');
          onChange({ fieldId: e.target.value, op, value: null });
        }}
      >
        {fields.map((f) => (
          <option key={f.id} value={f.id}>
            {f.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Condition"
        className={`${CONTROL} w-auto`}
        value={condition.op}
        onChange={(e) => update(e.target.value as FieldConditionOp, raw)}
      >
        {ops.map((op) => (
          <option key={op} value={op}>
            {FIELD_CONDITION_LABELS[op]}
          </option>
        ))}
      </select>
      {needsValue(condition.op) ? (
        type === 'dropdown' || type === 'boolean' ? (
          <select
            aria-label="Value"
            className={`${CONTROL} w-auto min-w-32`}
            value={raw}
            onChange={(e) => update(condition.op, e.target.value)}
          >
            <option value="">Choose…</option>
            {(type === 'boolean' ? ['yes', 'no'] : (field?.options ?? [])).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label="Value"
            className={`${CONTROL} w-auto min-w-32 flex-1`}
            type={type === 'date' ? 'date' : 'text'}
            inputMode={type === 'number' || type === 'currency' ? 'decimal' : undefined}
            placeholder={type === 'currency' ? '$' : ''}
            value={raw}
            aria-invalid={parsed.ok ? undefined : true}
            onChange={(e) => update(condition.op, e.target.value)}
          />
        )
      ) : null}
      <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Remove this condition">
        Remove
      </Button>
      {!parsed.ok && raw !== '' ? (
        <p className="footnote w-full text-danger">{parsed.error}</p>
      ) : null}
    </div>
  );
}
